import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { ArticlePrices } from 'graphql-ts-types';
import type { BorderRadiusProps } from 'styled-system';
import { borderRadius } from 'styled-system';

import type { ArticleCouponProps } from '../../../article-price/components/ArticlePrice';
import LegalDrawerContent from '../../../article-price/components/LegalDrawerContent';
import { useOpenDrawer } from '../../hooks/drawer';
import { useLocalization } from '../../providers/LocaleProvider';
import { Button } from '../Button';
import Icon from '../Icon';

const ARTICLE_PRICE_LEGAL_DRAWER_ICON_TEST_ID = 'article-price-legal-drawer-icon';
const DEFAULT_ICON_SIZE = 32;

export interface ArticlePriceInfoIconProps {
  dataTestId?: string;
  hasValuePackLabel?: boolean;
  regularPrice?: ArticlePrices['regular'];
  specialPrice?: Partial<Omit<ArticlePrices['special'], 'validTill' | 'displayCountdown'>>;
  coupon?: Nullable<Pick<ArticleCouponProps, 'priceAfterDiscount'>>;
  discountedPrice?: number | null;
  iconSize?: number;
}

const InfoIconWrapper = styled(Button)<BorderRadiusProps>`
  ${borderRadius};
`;

const infoIconWrapperStyles: ComponentProps<typeof InfoIconWrapper> = {
  kind: 'bare',
  borderRadius: 'x-large',
};

const ArticlePriceInfoIcon = ({
  dataTestId = ARTICLE_PRICE_LEGAL_DRAWER_ICON_TEST_ID,
  hasValuePackLabel = false,
  regularPrice,
  specialPrice,
  coupon,
  iconSize = DEFAULT_ICON_SIZE,
  discountedPrice,
}: ArticlePriceInfoIconProps) => {
  const { t } = useLocalization();

  const openDrawer = useOpenDrawer(
    LegalDrawerContent({
      hasValuePackLabel,
      regularPrice,
      specialPrice,
      coupon,
      discountedPrice,
    }),
    {
      preventDefault: true,
      stopPropagation: true,
    }
  );

  return (
    <InfoIconWrapper
      {...infoIconWrapperStyles}
      onClick={openDrawer}
      aria-label={`${t('h24_article_price')} - ${t('h24_learn_more')}`}
    >
      <Icon size={iconSize} name="info" dataTestId={dataTestId} />
    </InfoIconWrapper>
  );
};

export default ArticlePriceInfoIcon;
